import type { StringMap } from 'corgi-types';

import { EMAIL_ERRORS_MAPPING } from '../../constants/email';
import { DEFAULT_FORM_ERROR } from '../../constants/form';
import type { Translate } from '../../providers/LocaleProvider';
import type { GraphQLExtension, GraphQLExtensions } from '../../types/graphql';
import { getSignupErrorTranslationKey } from '../signup';
import { isErrorWithExtensions } from './error-with-extensions';

type GraphQLCodeExtensions = StringMap<string>;

type GraphQLMessageExtensions = StringMap<GraphQLExtension>;

export const hasCodeExtension = (
  extensions: GraphQLExtensions
): extensions is GraphQLCodeExtensions => typeof extensions.code === 'string';

export const isEmailCodeExtension = (
  extensions: GraphQLExtensions
): extensions is GraphQLMessageExtensions =>
  typeof extensions.email === 'object' && !!extensions.email.code;

export const isExtensionWithMessage = (
  error: string | number | GraphQLExtension
): error is GraphQLExtension => typeof error === 'object' && !!error.message;

export const extractErrorMessage = (
  extensions: GraphQLExtension | GraphQLExtensions,
  translate: Translate
) => {
  const errors = Object.values(extensions);

  if (!errors.length) {
    return translate(DEFAULT_FORM_ERROR);
  }

  if (hasCodeExtension(extensions)) {
    return translate(getSignupErrorTranslationKey(extensions.code));
  }

  if (isEmailCodeExtension(extensions)) {
    const errorKey = extensions.email.code as keyof typeof EMAIL_ERRORS_MAPPING;

    return translate(EMAIL_ERRORS_MAPPING[errorKey] || DEFAULT_FORM_ERROR);
  }

  const [firstError] = errors;
  if (isExtensionWithMessage(firstError)) {
    return firstError.message;
  }

  if (isExtensionWithMessage(extensions as GraphQLExtension)) {
    return extensions.message as string;
  }

  return translate(DEFAULT_FORM_ERROR);
};

export const getMessageFromGqlError = (error: unknown, t: Translate) => {
  const extensions: GraphQLExtensions = isErrorWithExtensions(error) ? error.extensions : {};
  const message = extractErrorMessage(extensions, t);

  return message;
};
